import React, { useMemo, useState, useRef } from "react";
import DataTable from "../../components/Table/Table";
import { getUserMonitoring } from "../../services/thunks/userMonitoringThunks";
import { FiFlag } from "react-icons/fi";
import dayjs from "dayjs";
import CustomButton from "../../components/Buttons/CustomButton";

// Icons
import searchIcon from "../../assets/icons/search.svg";
import calendarIcon from "../../assets/icons/calendar.svg";

const UserMonitoring = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeFilters, setActiveFilters] = useState({
    search: "",
    startDate: "",
    endDate: "",
  });

  const handleApplyFilters = () => {
    setActiveFilters({
      search: searchQuery,
      startDate: startDate,
      endDate: endDate,
    });
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setActiveFilters({ search: "", startDate: "", endDate: "" });
    setRefreshTrigger((prev) => prev + 1);
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Username",
        dataIndex: "username",
        key: "username",
        width: "10%",
        render: (_, record) => {
          const fullName = `${record.firstName || ""} ${
            record.lastName || ""
          }`.trim();
          return (
            <div className="text-sm text-[#E2E2E2] font-public-sans flex flex-col">
              <span className="font-medium">
                {fullName || record.username || "—"}
              </span>
            </div>
          );
        },
      },
      {
        title: "User ID",
        dataIndex: "userId",
        key: "userId",
        width: "10%",
        render: (text) => {
          if (!text) {
            return (
              <div className="text-sm text-[#B6B6B6CC] font-inter">N/A</div>
            );
          }
          return (
            <div className="text-sm text-[#E2E2E2] font-public-sans font-medium">
              {text}
            </div>
          );
        },
      },
      {
        title: "Login Time",
        dataIndex: "loginTime",
        key: "loginTime",
        width: "10%",
        render: (text) => (
          <div className="text-sm text-[#E2E2E2] font-public-sans flex flex-col">
            <span>{text ? dayjs(text).format("YYYY-MM-DD") : "—"}</span>
            <span>{text ? dayjs(text).format("HH:mm A") : ""}</span>
          </div>
        ),
      },
      {
        title: "Device & IP Address",
        key: "ipAndDevice",
        width: "20%",
        render: (_, record) => (
          <div className="text-sm text-[#E2E2E2] font-public-sans flex flex-col">
            <span>{record.device || "—"}</span>
            <span>{record.ipAddress || "—"}</span>
          </div>
        ),
      },
      {
        title: "Browser",
        dataIndex: "browser",
        key: "browser",
        width: "20%",
        render: (text) => (
          <div className="text-sm text-[#E2E2E2] font-public-sans">
            {text || "—"}
          </div>
        ),
      },
      {
        title: "Flag",
        dataIndex: "flag",
        key: "flag",
        width: "30%",
        render: (flag, record) => {
          if (!flag || !flag.label)
            return (
              <div className="text-sm text-[#B6B6B6CC] font-inter">N/A</div>
            );

          return (
            <div className="flex items-center gap-2 py-1">
              {(record.isSuspicious || flag.isSuspicious) && (
                <FiFlag className="text-[#FF0000] text-lg fill-[#FF0000]" />
              )}
              <span className="text-[#E2E2E2] font-inter font-normal text-[14px] leading-[24px] tracking-[0%] vertical-align-middle">
                {flag.label}
              </span>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="px-6 py-10 min-h-screen text-white">
      <div className="flex flex-col gap-6">
        {/* Filters Section - Consistently Styled with SubscriptionFilter.jsx/Charts.jsx */}
        <div className="w-full bg-[#111111d6] border border-[#505050] rounded-[8px] p-6 mb-2">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-[#E3E3E3] font-public-sans font-bold text-[20px] leading-[22px]">
              Filters
            </h2>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr_auto] gap-6 items-end">
            {/* Date Range - Native pattern with custom styling */}
            <div className="flex flex-col gap-2">
              <label className="text-[#E3E3E3] text-[14px] leading-[22px] font-public-sans">
                Date Range
              </label>
              <div className="flex items-center gap-[10px] bg-[#45444433] border border-[#808080] rounded-[5px] px-3 py-[10px] h-[45px]">
                <div className="relative w-[110px]">
                  {/* Label/Display Input */}
                  <input
                    type="text"
                    readOnly
                    value={
                      startDate ? dayjs(startDate).format("DD-MM-YYYY") : ""
                    }
                    placeholder="DD-MM-YYYY"
                    className="w-full bg-transparent text-[14px] font-public-sans text-[#B6B6B6CC] pr-4 outline-none cursor-pointer uppercase placeholder:text-[#B6B6B6CC]"
                  />
                  {/* Invisible Date Picker Overlaid */}
                  <input
                    type="date"
                    ref={startDateRef}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  <img
                    src={calendarIcon}
                    alt="cal"
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-[14px] h-[14px] pointer-events-none"
                  />
                </div>
                <span className="text-[#B6B6B6CC]">-</span>
                <div className="relative w-[110px]">
                  {/* Label/Display Input */}
                  <input
                    type="text"
                    readOnly
                    value={endDate ? dayjs(endDate).format("DD-MM-YYYY") : ""}
                    placeholder="DD-MM-YYYY"
                    className="w-full bg-transparent text-[14px] font-public-sans text-[#B6B6B6CC] pr-4 outline-none cursor-pointer uppercase placeholder:text-[#B6B6B6CC]"
                  />
                  {/* Invisible Date Picker Overlaid */}
                  <input
                    type="date"
                    ref={endDateRef}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  <img
                    src={calendarIcon}
                    alt="cal"
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-[14px] h-[14px] pointer-events-none"
                  />
                </div>
              </div>
            </div>

            {/* Search - Custom Search Group aligned with Mockup */}
            <div className="flex flex-col gap-2">
              <label className="text-[#E3E3E3] text-[14px] leading-[22px] font-public-sans">
                Search
              </label>
              <div className="flex items-center relative h-[45px] bg-[#2D2D2D] border border-[#808080] rounded-[5px] px-3">
                <img
                  src={searchIcon}
                  alt="search"
                  className="w-5 h-5 text-[#808080A3]"
                />
                <input
                  type="text"
                  placeholder="Search User ID .."
                  className="bg-transparent outline-none text-sm ml-3 w-full text-[#E2E2E2] placeholder:text-[#808080A3] font-public-sans pr-20"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button
                  onClick={handleApplyFilters}
                  className="absolute right-0 px-5 h-full rounded-[4px] text-[11px] text-[#979798] hover:text-[#51F9EF] hover:border-[#51F9EF] transition-colors bg-[#272727] font-bold uppercase font-public-sans"
                >
                  SEARCH
                </button>
              </div>
            </div>

            {/* Action Buttons - Standard CustomButton tokens */}
            <div className="flex flex-row gap-3">
              <CustomButton
                useGradientBorder
                className="w-[123px] h-[45px] text-[18px] font-medium font-public-sans py-0 px-4"
                textColor="text-[#51F9EF]"
                gradient="bg-gradient-to-b from-[#51F9EF] to-[#1A7DD1]"
                onClick={handleApplyFilters}
              >
                Apply Filters
              </CustomButton>
              <CustomButton
                className="w-[100px] h-[45px] text-[14px] font-normal font-inter py-0 px-0 uppercase hover:bg-[#9FB0AF20] border-[#9FB0AF]"
                textColor="text-[#9FB0AF]"
                borderColor="border-[#9FB0AF]"
                onClick={handleClearAll}
              >
                Clear All
              </CustomButton>
            </div>
          </div>
        </div>

        {/* Table Section - Correct Heading Colors */}
        <div className="custom-table-container">
          <DataTable
            columns={tableColumns}
            fetchData={getUserMonitoring}
            refreshTrigger={refreshTrigger}
            headingBgColor="#0D141B"
            headingTextColor="#FFFFFF"
            customQueryParams={(params) => ({
              ...params,
              search: activeFilters.search.trim() || undefined,
              startDate: activeFilters.startDate,
              endDate: activeFilters.endDate,
            })}
            initialPageSize={10}
            tableStyle={{ borderRadius: "8px" }}
          />
        </div>
      </div>

      <style jsx="true">{`
        .custom-date::-webkit-calendar-picker-indicator {
          opacity: 0;
          cursor: pointer;
        }
        .ant-table-wrapper .ant-table {
          background: #191919 !important;
        }
        .ant-table-thead > tr > th {
          border-bottom: 1px solid #505050 !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #505050 !important;
        }
      `}</style>
    </div>
  );
};

export default UserMonitoring;
